import React from "react";
import { Tournament, TournamentStats as Stats } from "../types/tournament";
import {
  Trophy,
  TrendingUp,
  Award,
  BarChart3,
  Users,
  Calendar,
} from "lucide-react";

interface TournamentStatsProps {
  stats: Stats;
  tournament: Tournament;
}

const TournamentStats: React.FC<TournamentStatsProps> = ({
  stats,
  tournament,
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTournamentDuration = () => {
    const start = tournament.createdAt;
    const end = tournament.updatedAt;
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Tournament Statistics
        </h3>
        <p className="text-gray-600 mb-6">
          Complete tournament overview and awards
        </p>
      </div>

      {/* Awards Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Champion */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Trophy className="w-8 h-8 text-yellow-600 mr-3" />
            <h4 className="text-lg font-semibold text-yellow-800">Champion</h4>
          </div>
          {stats.champion ? (
            <div>
              <p className="text-2xl font-bold text-yellow-900 mb-2">
                {stats.champion.name}
              </p>
              <p className="text-yellow-700 text-sm mb-2">
                {stats.champion.player1} & {stats.champion.player2}
              </p>
              <p className="text-yellow-600 text-sm">
                Record: {stats.champion.wins}W - {stats.champion.losses}L
              </p>
            </div>
          ) : (
            <p className="text-yellow-700 text-sm">Tournament in progress...</p>
          )}
        </div>

        {/* Most Wins */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Award className="w-8 h-8 text-green-600 mr-3" />
            <h4 className="text-lg font-semibold text-green-800">Most Wins</h4>
          </div>
          {stats.mostWins ? (
            <div>
              <p className="text-2xl font-bold text-green-900 mb-2">
                {stats.mostWins.name}
              </p>
              <p className="text-green-700 text-sm mb-2">
                {stats.mostWins.player1} & {stats.mostWins.player2}
              </p>
              <p className="text-green-600 text-sm">
                {stats.mostWins.wins} wins •{" "}
                {(
                  (stats.mostWins.wins /
                    (stats.mostWins.wins + stats.mostWins.losses)) *
                  100
                ).toFixed(1)}
                % win rate
              </p>
            </div>
          ) : (
            <p className="text-green-700 text-sm">No games played yet</p>
          )}
        </div>

        {/* Biggest Climb */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
            <h4 className="text-lg font-semibold text-blue-800">
              Biggest Climb
            </h4>
          </div>
          {stats.biggestClimb ? (
            <div>
              <p className="text-2xl font-bold text-blue-900 mb-2">
                {stats.biggestClimb.name}
              </p>
              <p className="text-blue-700 text-sm mb-2">
                {stats.biggestClimb.player1} & {stats.biggestClimb.player2}
              </p>
              <p className="text-blue-600 text-sm">
                Moved up {Math.abs(stats.biggestClimb.totalMovement)} courts
              </p>
            </div>
          ) : (
            <p className="text-blue-700 text-sm">No movement yet</p>
          )}
        </div>
      </div>

      {/* Tournament Overview */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Tournament Overview
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="bg-primary-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {tournament.teams.length}
            </p>
            <p className="text-sm text-gray-600">Teams</p>
          </div>

          <div className="text-center">
            <div className="bg-success-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
              <Calendar className="w-6 h-6 text-success-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {tournament.currentRound - 1}
            </p>
            <p className="text-sm text-gray-600">Rounds Completed</p>
          </div>

          <div className="text-center">
            <div className="bg-warning-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
              <Trophy className="w-6 h-6 text-warning-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats.totalGamesPlayed}
            </p>
            <p className="text-sm text-gray-600">Games Played</p>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats.averageGamesPerTeam.toFixed(1)}
            </p>
            <p className="text-sm text-gray-600">Avg Games/Team</p>
          </div>
        </div>
      </div>

      {/* Tournament Details */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Tournament Details
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-gray-900 mb-3">
              Basic Information
            </h5>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-gray-600">Tournament Name:</dt>
                <dd className="font-medium">{tournament.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Number of Courts:</dt>
                <dd className="font-medium">{tournament.courts.length}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Total Rounds:</dt>
                <dd className="font-medium">{tournament.totalRounds}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Current Round:</dt>
                <dd className="font-medium">{tournament.currentRound}</dd>
              </div>
            </dl>
          </div>

          <div>
            <h5 className="font-medium text-gray-900 mb-3">Timeline</h5>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-gray-600">Started:</dt>
                <dd className="font-medium">
                  {formatDate(tournament.createdAt)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Last Updated:</dt>
                <dd className="font-medium">
                  {formatDate(tournament.updatedAt)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Duration:</dt>
                <dd className="font-medium">{getTournamentDuration()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Status:</dt>
                <dd
                  className={`font-medium ${
                    tournament.currentRound > tournament.totalRounds
                      ? "text-success-600"
                      : "text-primary-600"
                  }`}
                >
                  {tournament.currentRound > tournament.totalRounds
                    ? "Complete"
                    : "In Progress"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Team Performance Summary */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Team Performance Summary
        </h4>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Team
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Starting Court
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Current Court
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Movement
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Record
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tournament.teams.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {team.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {team.player1} & {team.player2}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    Court {team.startingCourt}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    Court {team.currentCourt}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`text-sm font-medium ${
                        team.totalMovement < 0
                          ? "text-success-600"
                          : team.totalMovement > 0
                          ? "text-red-600"
                          : "text-gray-500"
                      }`}
                    >
                      {team.totalMovement < 0 ? "+" : ""}
                      {-team.totalMovement}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {team.wins}W - {team.losses}L
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TournamentStats;
